import { Inject, Injectable } from '@angular/core';
import { OrgAdminOrganizationService } from '../../../ispm-admin/app/org-admin-settings/organizations/services/org-admin-organization.service';
import {
  bpmnConnectingResourcesTypeKey,
  bpmnLinkedResourcesApprovalStatusesKey, bpmnLinkedResourcesTypeKey,
  pathwaysSelectionJobName
} from '@ispm-client/modules/pathways';
import { forkJoin } from 'rxjs';
import { filter, map, take } from 'rxjs/operators';
import { of } from 'rxjs/observable/of';
import { Observable } from 'rxjs/Rx';
import { WorkflowService } from '@ispm/workflow/services/workflow.service';
import { PathwaysConfigurationService, PathwaysService } from '@ispm-client/modules/pathways/services';
import { UserActionCreatorService } from '@ispm/user/services/user-action-creator.service';
import { CurationService } from '../../curation/services/curation.service';
import { BpmnXmlService } from '@ispm/shared/bpmn/services/bpmn-xml.service';
import { IRevision } from '@ispm/workflow/interfaces/revision.interface';
import { get } from 'lodash';
import { LogService } from '@ispm/core/services/log.service';
import {FormHandlerService} from "@ispm/dynamic-form/services/form-handler.service";
import {TestService} from '@ispm/dashboard/test/services/test.service';
import {DiagnosticOrderService} from "@ispm/test/services/diagnostic-order.service";

@Injectable()
export class PrefetchingService {

  private readonly pathToBPMNNamesToPrefetch = ['organizationConfiguration', 'prefetch', 'pathwaysByDisciplines'];
  private readonly testTypeToPrefetch = 'Pathway Test';
  private readonly prefetchFormForPathwayTest = ['GUIDANCE_NOTES-PROVIDER'];

  constructor(
    private orgAdminOrganizationService: OrgAdminOrganizationService,
    private workflowService: WorkflowService,
    private pathwaysService: PathwaysService,
    private pathwayConfigService: PathwaysConfigurationService,
    private userActionCreatorService: UserActionCreatorService,
    private curationService: CurationService,
    private bpmnXmlService: BpmnXmlService,
    private logservice: LogService,
    private formHandlerService: FormHandlerService,
    private diagnosticOrderService: DiagnosticOrderService,
    private testService : TestService
  ) {
  }

  prefetchOrganization(): void {
    this.orgAdminOrganizationService.getOrganizations('dept,lab,prov');
  }

  prefetchTestCollections(): void {
    this.testService.loadTestInfoCollections();
  }

  prefetchResourcesForTest(): void {
    forkJoin({
      template: this.fetchWorkflowJobsTemplates().pipe(take(1)),
      frequentlyUsedPathways: this.getBPMNNamesToPrefetch().pipe(take(1))
    })
      .pipe(
        filter(templateAndPathways => get(templateAndPathways.frequentlyUsedPathways, 'length', 0) > 0)
      )
      .subscribe(templateAndPathways => this.prefetchFrequentlyUsedPathways(templateAndPathways.template));
  }

  private prefetchFrequentlyUsedPathways(template): void {
    if (!template) {
      return;
    }
    this.prefetchResourcesForClinicalTrials(template)
      .subscribe(resourcesForClinicalTrials => {
        resourcesForClinicalTrials.pathways.forEach(discipline => {
          discipline.forEach(curation => {
            this.fetchBpmnTree(curation, resourcesForClinicalTrials.knowledgeBase);
            this.fetchClinicalTrials(resourcesForClinicalTrials, curation);
          });
        });
      });
  }

  private fetchBpmnTree(curation: any, knowledgeBase: string): void {
    this.bpmnXmlService.getBpmnXml(curation.deploymentId, curation.name, knowledgeBase, false).subscribe(
      () => {
        this.logservice.printInAutomationEnv(`bpmn tree prefetched: ${curation.name}`);
      });
  }

  private getBPMNNamesToPrefetch() {
    return this.userActionCreatorService.getState(...this.pathToBPMNNamesToPrefetch);
  }

  private fetchWorkflowJobsTemplates(): Observable<IRevision> {
    return this.userActionCreatorService.getCurrentUser()
      .mergeMap(user => this.prefetchWorkflowJobTemplatesAndForms(user.organization))
      .mergeMap(workflowJobsTemplates => this.workflowService.getWorkflowJobTemplate(workflowJobsTemplates ? workflowJobsTemplates.reference : null, false));
  }

  private prefetchResourcesForClinicalTrials(templates): Observable<any> {
    const uiSelectPathwayItem = templates.items.find(template => template.name === pathwaysSelectionJobName);
    return forkJoin(
      {
        knowledgeBase: this.pathwayConfigService.getKnowledgeBase().pipe(take(1)),
        clinicalTrialsSources: this.pathwayConfigService.getClinicalTrialsSources().pipe(take(1)),
        clinicalTrialStatusSearchParams: this.pathwayConfigService.getClinicalTrialStatusSearchParams().pipe(take(1)),
        placementStatuses: this.pathwayConfigService.getBpmnConnectingResourcesApprovalStatuses().pipe(take(1)),
        pathways: this.getPathwaysByName().pipe(take(1)),
        uiSelectPathwayItemInputs: of(uiSelectPathwayItem.inputs)
      });
  }

  private fetchClinicalTrials(paramsForClinicalTrials, curation): void {
    const clinicalTrialStatusesInput = paramsForClinicalTrials.uiSelectPathwayItemInputs.find(
      input => input.key === bpmnLinkedResourcesApprovalStatusesKey);
    const bpmnConnectingResourcesTypeInput = paramsForClinicalTrials.uiSelectPathwayItemInputs.find(
      input => input.key === bpmnConnectingResourcesTypeKey);
    const bpmnLinkedResourcesTypeInput = paramsForClinicalTrials.uiSelectPathwayItemInputs.find(
      input => input.key === bpmnLinkedResourcesTypeKey);

    this.pathwaysService.getClinicalTrials(
      curation.deploymentId,
      clinicalTrialStatusesInput.value,
      paramsForClinicalTrials.placementStatuses,
      bpmnLinkedResourcesTypeInput.value,
      bpmnConnectingResourcesTypeInput.value,
      paramsForClinicalTrials.knowledgeBase,
      paramsForClinicalTrials.clinicalTrialsSources,
      paramsForClinicalTrials.clinicalTrialStatusSearchParams
    ).subscribe(() => {
      this.logservice.printInAutomationEnv(`clinical trials prefetched`);
    });
  }

  private getPathwaysByName(): Observable<Array<any>> {
    return this.userActionCreatorService.getState(...this.pathToBPMNNamesToPrefetch)
      .mergeMap(bpmnTreesToPrefetch => {
        const requests = [];
        bpmnTreesToPrefetch.forEach((discipline) => {
          requests.push(
            this.curationService.listCurationNamesByDisciplineAndDisease(discipline.discipline, discipline.diseaseCodes).pipe(map(res => res.json()))
          );
        });
        return forkJoin(...requests);
      });
  }

  private preFetchForms(formIds: Array<string>) {
    if (formIds) {
      formIds.forEach(formId => this.formHandlerService.getForm(formId));
    }
  }

  private prefetchWorkflowJobTemplatesAndForms(orgId: string) {

    return this.diagnosticOrderService.getTemplates({OrganizationId: orgId}).pipe(map(data => {
      const testConfig = data.json().find(template => template.name === this.testTypeToPrefetch);
      if (!testConfig) {
        return {
          then: function () {
          }
        };
      }
      this.testService.setTestConfiguration(testConfig);
      if (testConfig.TreatmentLayout && testConfig.TreatmentLayout.forms) {
        this.preFetchForms(testConfig.TreatmentLayout.forms);
      }
      this.preFetchForms(this.prefetchFormForPathwayTest);
      return testConfig.workflowJobsTemplates.find(jobTemplate => jobTemplate.usage === 'revision')
    }));
  }
}
